/**
 * New Proposal form — /proposals/new
 */
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { useAuth } from '../context/AuthContext';
import { Loader2, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const oneWeekFromNow = () => {
  const d = new Date();
  d.setDate(d.getDate() + 14);
  d.setHours(23, 59, 0, 0);
  return d.toISOString().slice(0, 16); // "YYYY-MM-DDTHH:mm" for datetime-local
};

export default function NewProposal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: '',
    body: '',
    category: 'policy',
    closes_at: oneWeekFromNow(),
  });
  const [saving, setSaving] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  if (!user) {
    return (
      <div className="max-w-lg mx-auto py-16 px-4 text-center">
        <p className="mb-4">Sign in to propose something.</p>
        <Button asChild><Link to="/auth/login">Sign in</Link></Button>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const closesIso = new Date(form.closes_at).toISOString();
      const res = await axios.post(`${API}/proposals`, {
        title: form.title.trim(),
        body: form.body.trim(),
        category: form.category,
        closes_at: closesIso,
      });
      toast.success('Draft saved. Publish it when you\'re ready.');
      navigate(`/proposals/${res.data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8" data-testid="new-proposal-page">
      <SEO title="New proposal — Club Governance" description="Draft a new club proposal." />
      <Button variant="ghost" asChild className="mb-4">
        <Link to="/proposals"><ArrowLeft className="mr-2 h-4 w-4" />Back to proposals</Link>
      </Button>
      <Card>
        <CardContent className="p-6">
          <h1 className="text-2xl font-bold text-slate-900 mb-1">New proposal</h1>
          <p className="text-sm text-muted-foreground mb-6">
            Start as a draft. Publish when ready → members second it → officers open voting → vote → outcome.
          </p>
          <form onSubmit={submit} className="space-y-4" data-testid="new-proposal-form">
            <div>
              <Label htmlFor="title" className="text-xs">Title</Label>
              <Input
                id="title"
                required
                minLength={6}
                maxLength={140}
                value={form.title}
                onChange={upd('title')}
                placeholder="e.g. Adopt a founding cohort badge"
                data-testid="new-proposal-title"
              />
            </div>
            <div>
              <Label htmlFor="body" className="text-xs">Body (markdown ok — up to 8000 chars)</Label>
              <Textarea
                id="body"
                required
                minLength={20}
                maxLength={8000}
                rows={10}
                value={form.body}
                onChange={upd('body')}
                placeholder="What are we proposing, why, and how? Keep it clear."
                data-testid="new-proposal-body"
              />
            </div>
            <div>
              <Label className="text-xs">Category</Label>
              <RadioGroup
                value={form.category}
                onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}
                className="grid grid-cols-3 gap-2 mt-2"
              >
                {['policy', 'plan', 'project'].map((c) => (
                  <label
                    key={c}
                    className="flex items-center gap-2 border rounded-lg px-3 py-2 cursor-pointer hover:border-amber-400 has-[:checked]:border-amber-500 has-[:checked]:bg-amber-50"
                  >
                    <RadioGroupItem value={c} data-testid={`new-proposal-category-${c}`} />
                    <span className="text-sm capitalize">{c}</span>
                  </label>
                ))}
              </RadioGroup>
            </div>
            <div>
              <Label htmlFor="closes_at" className="text-xs">Voting closes</Label>
              <Input
                id="closes_at"
                type="datetime-local"
                required
                value={form.closes_at}
                onChange={upd('closes_at')}
                data-testid="new-proposal-closes"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Give members enough time to discuss and vote — 2 weeks is a good default.
              </p>
            </div>
            <Button
              type="submit"
              disabled={saving}
              className="w-full bg-amber-500 hover:bg-amber-600"
              data-testid="new-proposal-submit"
            >
              {saving ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</>
              ) : (
                'Save as draft'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
